import React, { useState } from 'react';
import { Users, FileText, TrendingUp, DollarSign, Activity, ArrowUp, ArrowDown, MoreVertical, Eye, Download, RefreshCw } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import HeaderTopSection from "../../components/partials/HeaderTop";
import '../../assets/css/DashboardPage.css';
import Banner from "../../components/partials/Banner";

const ClientDashboardPage = () => {
    const [timeRange, setTimeRange] = useState('7d');
    const [isLoading, setIsLoading] = useState(false);

    // Données simulées
    const stats = [
        {
            title: 'Utilisateurs Actifs',
            value: '12,458',
            change: '+12.5%',
            trend: 'up',
            icon: Users,
            color: '#3b82f6',
            bgColor: '#eff6ff'
        },
        {
            title: 'Contrats Signés',
            value: '847',
            change: '+8.2%',
            trend: 'up',
            icon: FileText,
            color: '#10b981',
            bgColor: '#f0fdf4'
        },
        {
            title: 'Revenus',
            value: '€234,567',
            change: '+15.3%',
            trend: 'up',
            icon: DollarSign,
            color: '#8b5cf6',
            bgColor: '#faf5ff'
        },
        {
            title: 'Taux de Conversion',
            value: '23.4%',
            change: '-2.1%',
            trend: 'down',
            icon: TrendingUp,
            color: '#f59e0b',
            bgColor: '#fffbeb'
        }
    ];

    const chartData = [
        { name: 'Lun', users: 4000, contracts: 240, revenue: 24000 },
        { name: 'Mar', users: 3000, contracts: 139, revenue: 22100 },
        { name: 'Mer', users: 2000, contracts: 980, revenue: 22900 },
        { name: 'Jeu', users: 2780, contracts: 390, revenue: 20000 },
        { name: 'Ven', users: 1890, contracts: 480, revenue: 21800 },
        { name: 'Sam', users: 2390, contracts: 380, revenue: 25000 },
        { name: 'Dim', users: 3490, contracts: 430, revenue: 21000 }
    ];

    const pieData = [
        { name: 'Nouveau', value: 45, color: '#3B82F6' },
        { name: 'Actif', value: 30, color: '#10B981' },
        { name: 'Inactif', value: 15, color: '#F59E0B' },
        { name: 'Suspendu', value: 10, color: '#EF4444' }
    ];

    const recentActivities = [
        { id: 1, user: 'Marie Dubois', action: 'a signé un contrat', time: 'Il y a 5 min', avatar: 'MD' },
        { id: 2, user: 'Jean Martin', action: 'a créé un compte', time: 'Il y a 12 min', avatar: 'JM' },
        { id: 3, user: 'Sophie Chen', action: 'a mis à jour son profil', time: 'Il y a 1h', avatar: 'SC' },
        { id: 4, user: 'Lucas Petit', action: 'a téléchargé un document', time: 'Il y a 2h', avatar: 'LP' },
        { id: 5, user: 'Emma Moreau', action: 'a annulé son abonnement', time: 'Il y a 3h', avatar: 'EM' }
    ];

    const handleRefresh = () => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 1000);
    };

    return (
        <>
            <HeaderTopSection />
            <Banner
                title="Mon tableau de bord"
                backgroundImage="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
            />

            <div className="dashboard-container">
                {/* Header Dashboard */}
                <div className="dashboard-header">
                    <div className="dashboard-header-content">
                        <div>
                            <h1 className="dashboard-header-title">Tableau de Bord</h1>
                            <p className="dashboard-header-subtitle">Vue d'ensemble de vos données et statistiques</p>
                        </div>
                        <div className="dashboard-header-controls">
                            <select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                className="dashboard-select"
                            >
                                <option value="7d">7 derniers jours</option>
                                <option value="30d">30 derniers jours</option>
                                <option value="90d">3 derniers mois</option>
                            </select>
                            <button
                                onClick={handleRefresh}
                                disabled={isLoading}
                                className="dashboard-refresh-button"
                            >
                                <RefreshCw style={{
                                    width: '1rem',
                                    height: '1rem',
                                    animation: isLoading ? 'spin 1s linear infinite' : 'none'
                                }} />
                                <span>Actualiser</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="dashboard-content">
                    {/* Stats Cards */}
                    <div className="dashboard-stats-grid">
                        {stats.map((stat, index) => (
                            <div
                                key={index}
                                className="dashboard-stat-card"
                            >
                                <div className="dashboard-stat-card-header">
                                    <div className="dashboard-stat-icon" style={{ backgroundColor: stat.bgColor }}>
                                        <stat.icon style={{ width: '1.5rem', height: '1.5rem', color: stat.color }} />
                                    </div>
                                    <button style={{
                                        padding: '0.25rem',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        borderRadius: '0.25rem',
                                        cursor: 'pointer'
                                    }}>
                                        <MoreVertical style={{ width: '1rem', height: '1rem', color: '#9ca3af' }} />
                                    </button>
                                </div>
                                <div>
                                    <h3 className="dashboard-stat-title">{stat.title}</h3>
                                    <p className="dashboard-stat-value">{stat.value}</p>
                                    <div className={`dashboard-stat-change ${stat.trend === 'up' ? 'dashboard-stat-change-up' : 'dashboard-stat-change-down'}`}>
                                        {stat.trend === 'up' ?
                                            <ArrowUp style={{ width: '1rem', height: '1rem', marginRight: '0.25rem' }} /> :
                                            <ArrowDown style={{ width: '1rem', height: '1rem', marginRight: '0.25rem' }} />
                                        }
                                        <span>{stat.change}</span>
                                        <span className="dashboard-stat-change-text">vs période précédente</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Charts Row */}
                    <div className="dashboard-charts-grid">
                        {/* Line Chart */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Évolution des Utilisateurs</h3>
                                    <p className="dashboard-chart-subtitle">Croissance sur les 7 derniers jours</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <button style={{
                                        padding: '0.5rem',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer'
                                    }}>
                                        <Eye style={{ width: '1rem', height: '1rem', color: '#6b7280' }} />
                                    </button>
                                    <button style={{
                                        padding: '0.5rem',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        borderRadius: '0.5rem',
                                        cursor: 'pointer'
                                    }}>
                                        <Download style={{ width: '1rem', height: '1rem', color: '#6b7280' }} />
                                    </button>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" stroke="#9CA3AF" />
                                    <YAxis stroke="#9CA3AF" />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="users"
                                        stroke="#3B82F6"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="url(#colorUsers)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Pie Chart */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Répartition des Utilisateurs</h3>
                                    <p className="dashboard-chart-subtitle">Par statut d'activité</p>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="dashboard-pie-chart-legend">
                                {pieData.map((item, index) => (
                                    <div key={index} className="dashboard-legend-item">
                                        <div className="dashboard-legend-label">
                                            <div className="dashboard-legend-color" style={{ backgroundColor: item.color }}></div>
                                            <span>{item.name}</span>
                                        </div>
                                        <span className="dashboard-legend-value">{item.value}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Bottom Row */}
                    <div className="dashboard-bottom-grid">
                        {/* Revenue Chart */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Revenus par Jour</h3>
                                    <p className="dashboard-chart-subtitle">Évolution des revenus cette semaine</p>
                                </div>
                            </div>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="name" stroke="#9CA3AF" />
                                    <YAxis stroke="#9CA3AF" />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                        }}
                                    />
                                    <Bar dataKey="revenue" fill="#10B981" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Recent Activities */}
                        <div className="dashboard-chart-card">
                            <div className="dashboard-chart-header">
                                <div>
                                    <h3 className="dashboard-chart-title">Activités Récentes</h3>
                                    <p className="dashboard-chart-subtitle">Dernières actions des utilisateurs</p>
                                </div>
                                <button style={{
                                    color: '#2563eb',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}>
                                    Voir tout
                                </button>
                            </div>
                            <div className="dashboard-activities-list">
                                {recentActivities.map((activity) => (
                                    <div
                                        key={activity.id}
                                        className="dashboard-activity-item"
                                    >
                                        <div className="dashboard-activity-avatar">
                                            {activity.avatar}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p className="dashboard-activity-text">
                                                <span style={{ fontWeight: '500' }}>{activity.user}</span> {activity.action}
                                            </p>
                                            <p className="dashboard-activity-time">{activity.time}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ClientDashboardPage;